import { useCallback, useMemo, useRef, useState } from 'react'

import type { F0FormRef } from '@factorialco/f0-react'
import type {
  PoFieldDefinitionDetailFragment,
  PurchaseOrderDetailFieldsFragment,
} from 'generated/resources/graphql'

/** How editing was entered: 'create' = "Review and Create" (draft→pending), 'edit' = pencil icon (save in place). */
export type EditMode = 'create' | 'edit' | null

export interface LineItemFieldValueDraft {
  fieldDefinitionId: string
  value: string
  displayValue?: string
}

export interface LineItemDraft {
  id?: string
  position: number
  fieldValues: LineItemFieldValueDraft[]
}

function buildInitialLineItems(
  purchaseOrder: PurchaseOrderDetailFieldsFragment | undefined
): LineItemDraft[] {
  if (!purchaseOrder) return []
  const sorted = [...purchaseOrder.poLineItems].sort((a, b) => a.position - b.position)
  return sorted.map((li, index) => ({
    id: li.id,
    position: index + 1,
    fieldValues: li.poFieldValues.reduce<LineItemFieldValueDraft[]>((acc, fv) => {
      if (fv.fieldDefinition) {
        acc.push({
          fieldDefinitionId: fv.fieldDefinition.id,
          value: fv.value ?? '',
          displayValue: fv.displayValue ?? undefined,
        })
      }
      return acc
    }, []),
  }))
}

interface UseEditPurchaseOrderReturn {
  editMode: EditMode
  isEditing: boolean
  lineItems: LineItemDraft[]
  formRef: React.MutableRefObject<F0FormRef | null>
  lineItemsRef: React.MutableRefObject<LineItemDraft[]>
  startEditing: (mode?: 'create' | 'edit') => void
  cancelEditing: () => void
  setLineItemFieldValue: (
    lineItemIndex: number,
    fieldDefinitionId: string,
    value: string,
    displayValue?: string
  ) => void
  addLineItem: (columnDefinitions: PoFieldDefinitionDetailFragment[]) => void
  removeLineItem: (lineItemIndex: number) => void
  replaceLineItems: (items: LineItemDraft[]) => void
  onEditSuccess: () => void
}

export function useEditPurchaseOrder(
  purchaseOrder: PurchaseOrderDetailFieldsFragment | undefined
): UseEditPurchaseOrderReturn {
  const [editMode, setEditMode] = useState<EditMode>(null)
  const isEditing = editMode !== null
  const [lineItems, setLineItems] = useState<LineItemDraft[]>(() =>
    buildInitialLineItems(purchaseOrder)
  )

  const formRef = useRef<F0FormRef | null>(null)
  const lineItemsRef = useRef<LineItemDraft[]>(lineItems)

  // Keep lineItemsRef in sync with state
  lineItemsRef.current = lineItems

  const startEditing = useCallback(
    (mode: 'create' | 'edit' = 'edit') => {
      setLineItems(buildInitialLineItems(purchaseOrder))
      setEditMode(mode)
    },
    [purchaseOrder]
  )

  const cancelEditing = useCallback(() => {
    setEditMode(null)
    setLineItems(buildInitialLineItems(purchaseOrder))
  }, [purchaseOrder])

  const setLineItemFieldValue = useCallback(
    (lineItemIndex: number, fieldDefinitionId: string, value: string, displayValue?: string) => {
      setLineItems((prev) => {
        const nextItems = [...prev]
        const item = nextItems[lineItemIndex]
        if (!item) return prev

        const existingIndex = item.fieldValues.findIndex(
          (fv) => fv.fieldDefinitionId === fieldDefinitionId
        )
        const nextFieldValues = [...item.fieldValues]
        const newEntry: LineItemFieldValueDraft = { fieldDefinitionId, value, displayValue }
        if (existingIndex >= 0) {
          nextFieldValues[existingIndex] = newEntry
        } else {
          nextFieldValues.push(newEntry)
        }

        nextItems[lineItemIndex] = { ...item, fieldValues: nextFieldValues }
        return nextItems
      })
    },
    []
  )

  const addLineItem = useCallback((columnDefinitions: PoFieldDefinitionDetailFragment[]) => {
    setLineItems((prev) => {
      const newPosition = prev.length + 1
      const fieldValues: LineItemFieldValueDraft[] = columnDefinitions
        .filter((d) => !d.computed && d.fieldKey !== 'line_number')
        .map((d) => ({ fieldDefinitionId: d.id, value: '' }))

      return [...prev, { position: newPosition, fieldValues }]
    })
  }, [])

  const removeLineItem = useCallback((lineItemIndex: number) => {
    setLineItems((prev) =>
      prev
        .filter((_, idx) => idx !== lineItemIndex)
        .map((item, idx) => ({ ...item, position: idx + 1 }))
    )
  }, [])

  const replaceLineItems = useCallback((items: LineItemDraft[]) => {
    setLineItems(items)
  }, [])

  const onEditSuccess = useCallback(() => {
    setEditMode(null)
  }, [])

  return useMemo(
    () => ({
      editMode,
      isEditing,
      lineItems,
      formRef,
      lineItemsRef,
      startEditing,
      cancelEditing,
      setLineItemFieldValue,
      addLineItem,
      removeLineItem,
      replaceLineItems,
      onEditSuccess,
    }),
    [
      editMode,
      isEditing,
      lineItems,
      startEditing,
      cancelEditing,
      setLineItemFieldValue,
      addLineItem,
      removeLineItem,
      replaceLineItems,
      onEditSuccess,
    ]
  )
}
